import { useEffect, useRef } from "react";

/** Decorative wave field. No dependencies, interaction, or SDK coupling. */
export function ParticleBackground({ prominent = false, theme }: { prominent?: boolean; theme: "light" | "dark" }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let width = 0;
    let height = 0;
    let lastFrame = 0;
    let phase = 0;
    let color = "";

    const paint = () => {
      context.clearRect(0, 0, width, height);
      context.fillStyle = color;
      // A perspective grid, folded into gently moving ribbons.
      const columns = width < 640 ? 64 : 110;
      const rows = 32;
      for (let row = 0; row < rows; row++) {
        const depth = row / (rows - 1);
        for (let column = 0; column < columns; column++) {
          const u = column / (columns - 1);
          const wave = Math.sin(u * 12 + depth * 5 + phase) * 0.55
            + Math.sin(u * 21 - depth * 8 - phase * 0.65) * 0.25;
          const x = (u - 0.5) * width * (1.1 + depth * 0.35) + width / 2;
          const y = height * (0.36 + depth * 0.63) + wave * (35 + depth * 65);
          context.globalAlpha = (0.12 + depth * 0.5) * (0.55 + Math.max(0, wave) * 0.45);
          context.beginPath();
          context.arc(x, y, 0.55 + depth * 1.15, 0, Math.PI * 2);
          context.fill();
        }
      }
      context.globalAlpha = 1;
    };

    const animate = (now: number) => {
      // Cap drawing at 30fps and time deltas after a suspended tab.
      if (now - lastFrame >= 1000 / 30) {
        phase += Math.min(now - lastFrame, 80) * 0.000195;
        lastFrame = now;
        paint();
      }
      frame = requestAnimationFrame(animate);
    };

    const restart = () => {
      cancelAnimationFrame(frame);
      paint();
      if (!motion.matches && !document.hidden) {
        lastFrame = performance.now();
        frame = requestAnimationFrame(animate);
      }
    };

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      color = getComputedStyle(canvas).color;
      restart();
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    motion.addEventListener("change", restart);
    document.addEventListener("visibilitychange", restart);
    resize();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      motion.removeEventListener("change", restart);
      document.removeEventListener("visibilitychange", restart);
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="particle-background" data-prominent={prominent} aria-hidden="true" />;
}
